import React, { useEffect, useRef, useState } from 'react';
import { Music, Users, Utensils, Heart } from 'lucide-react';
import { getNextBandDateLabel } from '../utils/bandDateUtils';

const AboutSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const features = [
    { icon: Utensils, title: 'Great Food', desc: 'Made fresh, made right' },
    { icon: Music, title: 'Live Music', desc: getNextBandDateLabel() },
    { icon: Users, title: 'Private Parties', desc: 'Call to book' },
    { icon: Heart, title: 'New Ownership', desc: 'Same neighborhood love' },
  ];

  return (
    <section id="about" ref={sectionRef} className="py-20 lg:py-24 bg-[#FDF6E9] texture-paper relative overflow-hidden">
      {/* Top decorative divider */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#D4A574]/40 to-transparent" />
      
      {/* Background decoration */}
      <div className="hidden md:block absolute top-0 right-0 w-96 h-96 bg-[#C9A227]/5 rounded-full blur-3xl" />
      <div className="hidden md:block absolute bottom-0 left-0 w-80 h-80 bg-[#D4A574]/10 rounded-full blur-3xl" />
      
      <div className="relative z-10 max-w-6xl mx-auto px-5">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left content */}
          <div className={`transition-[opacity,transform] duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <span className="text-[#C9A227] font-semibold tracking-widest text-sm uppercase">Our Story</span>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#2C2416] tracking-wide mt-3 leading-[0.95]">
              YOUR NEIGHBORHOOD
              <span className="block text-[#C9A227]">HANGOUT</span>
            </h2>
            
            <div className="mt-8 space-y-5 text-lg text-[#2C2416]/70 leading-relaxed">
              <p>
                Named after the legendary <span className="font-accent italic text-[#C9A227]">Louis "Satchmo" Armstrong</span>, 
                we bring that same spirit of joy, soul, and good vibes to everything we do.
              </p>
              <p>
                Under new ownership and completely refreshed, we've kept what makes a great 
                neighborhood spot — <span className="font-semibold text-[#2C2416]">good food, cold drinks, and great times</span>.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-10">
              {[
                { value: '2013', label: 'Established' },
                { value: '4.5★', label: 'Google' },
                { value: 'Real', label: 'Ingredients' },
              ].map((stat, i) => (
                <div
                  key={i}
                  className={`text-center p-4 rounded-lg stats-card cursor-default transition-[opacity,transform] duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                  style={{ transitionDelay: isVisible ? `${(i + 2) * 100}ms` : '0ms' }}
                >
                  <p className="font-display text-4xl text-[#C9A227]">{stat.value}</p>
                  <p className="text-sm text-[#2C2416]/70 mt-1 uppercase tracking-wide">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Feature cards */}
          <div className={`grid grid-cols-2 gap-4 transition-[opacity,transform] duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <div
                  key={i}
                  className={`bg-[#FFFBF5] p-6 border-2 border-[#2C2416]/10 hover:border-[#C9A227]/50 hover-lift transition-all duration-300 group ${
                    i % 2 === 1 ? 'translate-y-6' : ''
                  }`}
                  style={{ animationDelay: `${300 + i * 100}ms` }}
                >
                  <div className="w-14 h-14 bg-[#C9A227]/10 flex items-center justify-center mb-4 group-hover:bg-[#C9A227] transition-colors duration-300">
                    <Icon className="w-7 h-7 text-[#C9A227] group-hover:text-[#1a1612] transition-colors duration-300" />
                  </div>
                  <h3 className="font-display text-xl text-[#2C2416] tracking-wide">{feature.title}</h3>
                  <p className="text-sm text-[#2C2416]/75 mt-1">{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quote section */}
        <div className={`mt-20 text-center transition-[opacity,transform] duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="divider-ornament max-w-md mx-auto mb-8">
            <span className="text-3xl">🎺</span>
          </div>
          <blockquote className="font-accent text-2xl sm:text-3xl italic text-[#2C2416]/80 max-w-2xl mx-auto">
            "What a wonderful world"
          </blockquote>
          <p className="mt-3 text-[#C9A227] font-semibold">— Louis Armstrong</p>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
